import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import type { IMetaPage, MetaIntegration } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';
import { errorMessage, logError } from '@feathr/hooks';

/** Structured error with a specific title for display */
export interface IMetaIntegrationError {
  /** Short, specific title describing the error category */
  title: string;
  /** Detailed error message */
  message: string;
}

/** Return type for the useMetaIntegration hook */
export interface IUseMetaIntegration {
  /** Whether the integration data is being fetched */
  isLoading: boolean;
  /** Whether Meta is currently connected */
  isConnected: boolean;
  /** Whether the integration has a connection (configuring or connected) */
  hasConnection: boolean;
  /** Structured error if fetch or OAuth failed */
  error: IMetaIntegrationError | null;
  /** The MetaIntegration model instance, null before loaded or when fetch failed */
  integration: MetaIntegration | null;
  /** List of connected Facebook pages */
  pages: IMetaPage[];
  /** Callback to refetch integration data */
  refetch: () => Promise<void>;
}

/**
 * Hook to manage Meta integration state and data fetching.
 *
 * Handles fetching integration status, pages data, and OAuth error detection
 * from query parameters after redirect.
 *
 * @returns Integration state including loading, error, and data
 * @example
 * const { isConnected, integration, pages, refetch } = useMetaIntegration();
 */
export function useMetaIntegration(): IUseMetaIntegration {
  const { t } = useTranslation();
  const location = useLocation();
  const { MetaIntegrations } = useStore();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<IMetaIntegrationError | null>(null);
  const [integration, setIntegration] = useState<MetaIntegration | null>(null);
  const [pages, setPages] = useState<IMetaPage[]>([]);

  // Tracks the latest fetch call so stale responses from earlier calls are discarded
  const fetchIdRef = useRef(0);

  /**
   * Shared fetch logic used by both the mount effect and the refetch callback.
   * Accepts a cancellation check so each caller can control when results are discarded:
   * - The effect passes a closure over `isCancelled` (set on cleanup)
   * - The refetch callback passes a check against `fetchIdRef` (race protection)
   *
   * Error is NOT cleared here — callers are responsible for clearing it beforehand
   * so that OAuth errors can survive across the fetch when appropriate.
   */
  async function doFetch(shouldCancel: () => boolean): Promise<void> {
    setIsLoading(true);
    setPages([]);

    try {
      const metaIntegration = await MetaIntegrations.fetchIntegration();
      if (shouldCancel()) {
        return;
      }
      setIntegration(metaIntegration);

      // Only fetch pages if the integration has progressed past initial setup
      if (metaIntegration.isConnected || metaIntegration.isConfiguring) {
        try {
          const pagesData = await metaIntegration.getPages();
          if (shouldCancel()) {
            return;
          }
          setPages(pagesData);
        } catch (error_) {
          if (shouldCancel()) {
            return;
          }
          const pagesErrorMsg = errorMessage(error_, t);
          logError({
            message: 'Meta pages fetch failed',
            level: 'warning',
            extra: { error: pagesErrorMsg },
          });
          // Partial failure: integration loaded but pages didn't
          setError({
            title: t('Failed to Load Pages'),
            message: t('Connected, but failed to load Facebook pages'),
          });
        }
      }
    } catch (error_) {
      if (shouldCancel()) {
        return;
      }
      const errMsg = errorMessage(error_, t);
      logError({
        message: 'Meta integration fetch failed',
        level: 'error',
        extra: { error: errMsg },
      });
      setError({ title: t('Connection Error'), message: errMsg });
    } finally {
      if (!shouldCancel()) {
        setIsLoading(false);
      }
    }
  }

  // Increment fetchIdRef so any in-flight request from a prior call is ignored
  const refetch = useCallback(async (): Promise<void> => {
    const id = ++fetchIdRef.current;
    setError(null);
    await doFetch(() => fetchIdRef.current !== id);
  }, [MetaIntegrations, t]);

  // Runs on mount and whenever the query string changes (e.g. OAuth redirect)
  useEffect(() => {
    let isCancelled = false;

    // Check for OAuth error params that Facebook appends on failed authorization
    const searchParams = new URLSearchParams(location.search);
    const oauthError = searchParams.get('error');
    const oauthErrorDescription = searchParams.get('error_description');

    if (oauthError) {
      logError({
        message: 'Meta OAuth flow returned an error',
        level: 'warning',
        extra: {
          oauthError,
          oauthErrorDescription,
        },
      });
      // Prefer the human-readable description, fall back to the error code
      setError({
        title: t('Authorization Failed'),
        message: oauthErrorDescription || oauthError,
      });
    } else {
      setError(null);
    }

    // Always fetch integration data, even when there's an OAuth error,
    // so the page renders the correct connected/onboarding state
    doFetch(() => isCancelled);

    return () => {
      isCancelled = true;
    };
  }, [location.search, MetaIntegrations, t]);

  return {
    isLoading,
    isConnected: integration?.isConnected ?? false,
    hasConnection: integration !== null && (integration.isConnected || integration.isConfiguring),
    error,
    integration,
    pages,
    refetch,
  };
}
